import fs from "node:fs";
import path from "node:path";
import type { AgentRateLimit, RateLimitType } from "../../agent-sdk-types.js";

/**
 * Companion script written to `$APPOSTLE_HOME/internal/statusline-relay.cjs`.
 * The Claude Code CLI spawns it after every assistant turn via the
 * `settings.statusLine.command` escape hatch and pipes the documented
 * statusline JSON payload to its stdin. The payload includes
 * `rate_limits.{five_hour,seven_day}.{used_percentage,resets_at}` —
 * populated at ALL usage levels, not just near a limit, because the CLI
 * binary reads the undocumented `anthropic-ratelimit-unified-*` response
 * headers internally and surfaces them through this channel.
 *
 * This script reads stdin, writes the payload to a per-session file
 * (`APPOSTLE_STATUSLINE_OUT` env var), and prints an empty status line.
 * The daemon watches that file and feeds rate-limit updates back into the
 * agent's `usage_updated` event.
 */
const STATUSLINE_RELAY_SCRIPT = `"use strict";
const fs = require("node:fs");
const path = require("node:path");
let buffer = "";
process.stdin.setEncoding("utf8");
process.stdin.on("data", (chunk) => {
  buffer += chunk;
});
process.stdin.on("end", () => {
  try {
    const out = process.env.APPOSTLE_STATUSLINE_OUT;
    if (!out || !buffer) {
      process.stdout.write("");
      return;
    }
    const parsed = JSON.parse(buffer);
    const rate_limits = parsed && typeof parsed === "object" ? parsed.rate_limits : undefined;
    const payload = JSON.stringify({ rate_limits, capturedAt: Date.now() });
    fs.mkdirSync(path.dirname(out), { recursive: true });
    const tmp = out + ".tmp." + process.pid;
    fs.writeFileSync(tmp, payload);
    fs.renameSync(tmp, out);
  } catch (_err) {
    // Intentionally swallow — the statusline must not crash the host CLI.
  } finally {
    process.stdout.write("");
  }
});
process.stdin.on("error", () => {
  process.stdout.write("");
});
`;

const VALID_RATE_LIMIT_TYPES = new Set<RateLimitType>([
  "five_hour",
  "seven_day",
  "seven_day_opus",
  "seven_day_sonnet",
  "overage",
]);

/**
 * Write the relay script to `$APPOSTLE_HOME/internal/statusline-relay.cjs` if it
 * is missing or out-of-date. Returns the absolute path of the on-disk script.
 *
 * Idempotent: callers can invoke this on every session start without thrashing
 * the disk — content is only re-written if the hash differs.
 */
export function ensureStatuslineRelayScript(appostleHome: string): string {
  const dir = path.join(appostleHome, "internal");
  const file = path.join(dir, "statusline-relay.cjs");
  fs.mkdirSync(dir, { recursive: true });
  let existing: string | null = null;
  try {
    existing = fs.readFileSync(file, "utf8");
  } catch {
    existing = null;
  }
  if (existing !== STATUSLINE_RELAY_SCRIPT) {
    fs.writeFileSync(file, STATUSLINE_RELAY_SCRIPT, { mode: 0o644 });
  }
  return file;
}

/**
 * Build the absolute path of a per-session statusline payload file.
 */
export function buildStatuslineDataPath(appostleHome: string, sessionKey: string): string {
  return path.join(appostleHome, "internal", "statusline", `${sessionKey}.json`);
}

/**
 * Parse a statusline payload object (as written by the relay) into a typed
 * `AgentRateLimit[]`. Returns `null` if no usable buckets are present.
 *
 * Statusline schema (claude-code v1.x):
 *   { rate_limits: {
 *       five_hour:  { used_percentage: number, resets_at: number },
 *       seven_day:  { used_percentage: number, resets_at: number },
 *     },
 *     capturedAt: number }
 *
 * `used_percentage` is 0–100. We convert to 0.0–1.0 utilization to match the
 * existing `AgentRateLimit.utilization` contract (which mirrors the SDK's
 * `SDKRateLimitInfo.utilization`).
 */
export function parseStatuslinePayload(raw: unknown): AgentRateLimit[] | null {
  if (!raw || typeof raw !== "object") return null;
  const rateLimits = (raw as { rate_limits?: unknown }).rate_limits;
  if (!rateLimits || typeof rateLimits !== "object") return null;
  const entries = rateLimits as Record<string, unknown>;
  const result: AgentRateLimit[] = [];
  for (const [key, value] of Object.entries(entries)) {
    if (!VALID_RATE_LIMIT_TYPES.has(key as RateLimitType)) continue;
    if (!value || typeof value !== "object") continue;
    const bucket = value as { used_percentage?: unknown; resets_at?: unknown };
    const usedPercentage = bucket.used_percentage;
    if (typeof usedPercentage !== "number" || !Number.isFinite(usedPercentage)) continue;
    const utilization = usedPercentage / 100;
    const limit: AgentRateLimit = {
      rateLimitType: key as RateLimitType,
      utilization,
    };
    const resetsAt = bucket.resets_at;
    if (typeof resetsAt === "number" && Number.isFinite(resetsAt)) {
      limit.resetsAt = resetsAt;
    }
    result.push(limit);
  }
  return result.length > 0 ? result : null;
}
